<script lang="ts">
  import { byWeight, groupState, hiddenTally, onlyType, OTHER_GROUP, toggleType, typeBreakdown, type BlocksDone, type LinesDone, type TypeClassifier, type TypeCount } from '../logic/filefilter';
  import { compactCount } from '../logic/stackRail';

  interface Props {
    /** Groups present in the scope, with counts over the files the other filters leave. */
    types: TypeCount[];
    /** Selected group IDs and keys (`types=`). */
    selected: string[];
    classifier: TypeClassifier;
    onchange: (selected: string[]) => void;
    /** Lines reviewed per group and key (logic/filefilter `tallyProgress`): drawn around each chip's border. */
    progress?: Map<string, LinesDone>;
    /** Change blocks reviewed per group and key (logic/filefilter `tallyBlocks`): said with each type's weight. */
    blocks?: Map<string, BlocksDone>;
  }
  let { types: given, selected, classifier, onchange, progress, blocks }: Props = $props();

  /** Heaviest first: where the changed lines are, not how many files. */
  const types = $derived(byWeight(given, progress));
  /** Each type's hue on the weight bar and its chip, in weight order (Other muted). */
  const HUES = ['--syn-keyword', '--syn-call', '--syn-string', '--syn-type', '--syn-number', '--syn-flow', '--accent'];
  const hueOf = $derived.by(() => {
    const m = new Map<string, string>();
    types.filter((t) => t.group !== OTHER_GROUP).forEach((t, i) => m.set(t.group, `var(${HUES[i % HUES.length]})`));
    m.set(OTHER_GROUP, 'var(--muted)');
    return m;
  });
  /** The weight bar: each type's share of the changed lines, heaviest first; none until any lines are known. */
  const weights = $derived.by(() => {
    const all = types.map((t) => ({ t, lines: progress?.get(t.group)?.total ?? 0 })).filter((w) => w.lines > 0);
    const sum = all.reduce((n, w) => n + w.lines, 0);
    return sum ? all.map((w) => ({ ...w, share: w.lines / sum })) : [];
  });
  /** Whether `token`'s figures are still growing: some of its files' diffs are not computed yet (logic/pending). */
  const countingOf = (token: string) => (blocks?.get(token)?.unknown ?? 0) > 0;
  /** Whether the weights are still settling: any type's files not all counted. */
  const counting = $derived(types.some((t) => countingOf(t.group)));
  const uid = $props.id();

  /** Groups shown before "more" (selected ones always show). */
  const FIRST = 6;

  let expanded: Record<string, boolean> = $state({});
  let more = $state(false);

  const shown = $derived(more ? types : types.filter((t, i) => i < FIRST || groupState(selected, t.group, classifier) !== 'none'));
  const hidden = $derived(types.length - shown.length);
  /** What "+N more" holds, said together: its review progress, whether it is still counting, and each type for its tooltip. */
  const held = $derived.by(() => {
    const on = new Set(shown.map((t) => t.group));
    return hiddenTally(types.filter((t) => !on.has(t.group)), progress, blocks);
  });
  const heldShare = $derived(held.lines.total ? Math.floor((held.lines.done / held.lines.total) * 100) : null);
  /** Selected tokens no chip shows (no such files in this scope). */
  const absent = $derived.by(() => {
    const known = new Set(types.flatMap((t) => [t.group, ...t.keys.map((k) => k.key)]));
    return selected.filter((t) => !known.has(t));
  });

  function isOpen(t: TypeCount): boolean {
    return expanded[t.group] ?? groupState(selected, t.group, classifier) === 'some';
  }

  function pick(e: MouseEvent | KeyboardEvent, token: string) {
    onchange(e.altKey ? onlyType(token) : toggleType(selected, token, classifier));
  }

  // Alt+Enter / Alt+Space: "only this" from the keyboard (a keyboard click carries no modifier everywhere).
  function onkeydown(e: KeyboardEvent, token: string) {
    if (e.altKey && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      e.stopPropagation();
      onchange(onlyType(token));
    }
  }

  function plural(n: number) {
    return `${n} file${n === 1 ? '' : 's'}`;
  }
  /** `token`'s review progress, said after its file count; none with nothing to review. */
  function reviewed(token: string): string {
    const p = progress?.get(token);
    return p ? `, ${p.done} of ${p.total} lines reviewed${blocksLeft(token)}` : '';
  }
  /** `token`'s change blocks still to review, as a clause (a floor while some files are not counted); none when unknown. */
  function blocksLeft(token: string): string {
    const b = blocks?.get(token);
    if (!b || (!b.total && b.unknown)) return '';
    return `, ${b.unknown ? 'at least ' : ''}${b.total - b.done} of ${b.total} blocks left`;
  }
  /** A weight segment's tooltip: its lines, their share, and what is left of them. */
  function weightTitle(w: { t: TypeCount; lines: number; share: number }): string {
    const p = progress?.get(w.t.group);
    return `${w.t.name}: ${w.lines.toLocaleString('en-US')} changed lines (${Math.round(w.share * 100)}%)${p ? `, ${(p.total - p.done).toLocaleString('en-US')} unreviewed` : ''}${blocksLeft(w.t.group)}${countingOf(w.t.group) ? ' (still counting)' : ''}`;
  }
  /** The share of `token`'s lines reviewed, 0–100, or null with nothing to review. */
  function share(token: string): number | null {
    const p = progress?.get(token);
    return p ? Math.floor((p.done / p.total) * 100) : null;
  }
</script>

<div class="type-chips" role="group" aria-label="Filter by file type">
  {#if weights.length > 1}
    <!-- Where the change is: each type's share of the changed lines (the chips say the same numbers in their names). -->
    <div class="type-weights" class:counting-bar={counting} aria-hidden="true">
      {#each weights as w (w.t.group)}<span class="type-weight" style:flex-grow={w.share} style:background={hueOf.get(w.t.group)} title={weightTitle(w)}></span>{/each}
    </div>
  {/if}
  {#each shown as t (t.group)}
    {@const state = groupState(selected, t.group, classifier)}
    {@const open = isOpen(t)}
    {@const breakdown = typeBreakdown(t)}
    <span class="type-group" class:open>
      <button
        class="type-chip"
        class:on={state === 'all'}
        class:some={state === 'some'}
        class:empty={!t.count}
        class:progress={share(t.group) !== null}
        class:done={share(t.group) === 100}
        class:counting={countingOf(t.group)}
        style:--done={share(t.group)}
        aria-pressed={state === 'all' ? 'true' : state === 'some' ? 'mixed' : 'false'}
        aria-label="{t.name}, {plural(t.count)}{reviewed(t.group)}{countingOf(t.group) ? ', still counting' : ''}"
        aria-describedby={breakdown ? `${uid}-${t.group}` : undefined}
        title={`${t.name}: ${plural(t.count)}${reviewed(t.group)}.${breakdown ? `\n${breakdown}` : ''}\nAlt-click: only this type.`}
        onclick={(e) => pick(e, t.group)}
        onkeydown={(e) => onkeydown(e, t.group)}
        >{#if weights.length > 1}<i class="type-hue" style:background={hueOf.get(t.group)} aria-hidden="true"></i>{/if}{t.name}<span class="n" aria-hidden="true">{t.count}</span
        >{#if progress?.get(t.group)}<span class="lines" aria-hidden="true">{compactCount(progress.get(t.group)!.total)}</span>{/if}</button
      >{#if breakdown}<span class="sr-only" id="{uid}-{t.group}">{breakdown}</span>{/if}
      {#if t.keys.length > 1}
        <button
          class="type-expand"
          aria-expanded={open}
          aria-label="{open ? 'Hide' : 'Show'} {t.name} extensions"
          title="{open ? 'Hide' : 'Show'} {t.name} extensions"
          onclick={() => (expanded = { ...expanded, [t.group]: !open })}>{open ? '‹' : '›'}</button
        >
      {/if}
      {#if open && t.keys.length > 1}
        {#each t.keys as k (k.key)}
          {@const on = selected.includes(k.key)}
          <button
            class="type-chip key"
            class:on
            class:empty={!k.count}
            class:progress={share(k.key) !== null}
            class:done={share(k.key) === 100}
            style:--done={share(k.key)}
            aria-pressed={on}
            aria-label="{k.key}, {plural(k.count)}{reviewed(k.key)}"
            title="{k.key}: {plural(k.count)}{reviewed(k.key)}. Alt-click: only this."
            onclick={(e) => pick(e, k.key)}
            onkeydown={(e) => onkeydown(e, k.key)}>{k.key}<span class="n" aria-hidden="true">{k.count}</span></button
          >
        {/each}
      {/if}
    </span>
  {/each}
  {#each absent as token (token)}
    <button
      class="type-chip on empty"
      aria-pressed="true"
      aria-label="{classifier.isGroup(token) ? classifier.groupName(token) : token}, no files here"
      title="No such files in this list. Click to remove."
      onclick={() => onchange(selected.filter((t) => t !== token))}>{classifier.isGroup(token) ? classifier.groupName(token) : token}<span class="n" aria-hidden="true">0</span></button
    >
  {/each}
  {#if hidden > 0 || more}
    {#if more}
      <button class="type-more" aria-expanded={more} onclick={() => (more = !more)}>Fewer</button>
    {:else}
      <!-- The types it holds, together: their lines reviewed as the chips draw theirs, and still counting while any is. -->
      <button
        class="type-more"
        class:progress={heldShare !== null}
        class:counting={held.counting}
        style:--done={heldShare}
        aria-expanded={more}
        aria-label="{hidden} more types{held.lines.total ? `, ${held.lines.done} of ${held.lines.total} lines reviewed` : ''}{held.counting ? ', still counting' : ''}: {held.each.join('; ')}"
        title="{held.each.join('\n')}"
        onclick={() => (more = !more)}>+{hidden} more{#if held.lines.total}<span class="lines" aria-hidden="true">{compactCount(held.lines.total)}</span>{/if}</button
      >
    {/if}
  {/if}
  {#if selected.length}
    <button class="type-clear" title="Show every file type" onclick={() => onchange([])}>Clear</button>
  {/if}
</div>
